import React, { useState, useEffect, useMemo } from 'react'
import { Dialog } from '@headlessui/react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { RootState, AppDispatch, store } from '../../app/store'
import {
  selectOrderType,
  markBranchSelected,
  markAddressSelected,
  clearBranchSelection,
  clearAddressSelection,
  setSelectedAddressId,
  setCarDetails,
  loadOrderTypes,
  selectSelectedTypeId,
  updateOrderTypeFromId,
  updateCarColorSearch,
  updateCarTypeSearch,
  setAddressStep,
  setSelectedAddressType,
  setApartmentNumber,
  setSelectedLatLng,
  setSelectedAddressText,
  resetAddressForm,
  selectIsCsFlowComplete,
  markOrderDetailsCompleted,
} from '../../features/csFlow/csFlowSlice'
import {
  useGetEmployeeBranchesQuery,
  useGetCustomerAddressesQuery,
  useGetCarModelsAndColorsQuery,
  useChangeCartTypeMutation,
} from '../../features/csFlow/csFlowAPI'
import { refreshCsCartItems, selectCsCartId } from '../../features/csFlow/csCartSlice'
import toast from 'react-hot-toast'

// Import new components
import { buildOrderTypeSection, buildContinueButton } from './components/TakeawayComponents'
import { buildBranchSelectionContent, buildBranchSelectionSection } from './components/BranchComponents'
import {
  buildCarDetailsSection,
  buildCarColorStep,
  buildCarTypeStep,
} from './components/CarPickupComponents'
import {
  buildAddressSelectionSection,
  buildCompleteAddressFlow,
} from './components/DeliveryComponents'
import { canContinue as canContinueUtil, getHeaderTitle } from './utils/orderPopupUtils'

const normalizeBooleanValue = (value: any): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['true', '1', 'yes'].includes(normalized)) return true
    if (['false', '0', 'no'].includes(normalized)) return false
  }
  return false
}

interface CsOrderTypeSelectionPopupProps {
  isOpen: boolean
  onClose: () => void
}

type PopupStep = 'main' | 'branch' | 'address' | 'carColor' | 'carType'

const CsOrderTypeSelectionPopup: React.FC<CsOrderTypeSelectionPopupProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const [currentStep, setCurrentStep] = useState<PopupStep>('main')
  const [searchQuery, setSearchQuery] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    orderTypes,
    isOrderTypesLoading,
    selectedOrderType,
    isOrderTypeSelected,
    isBranchSelected,
    selectedBranchId,
    isAddressSelected,
    selectedAddressId,
    carColor,
    carBrand,
    carNumber,
    carModel,
    carLogo,
    carColorSearchQuery,
    carTypeSearchQuery,
    customerId,
    addressStep,
    selectedAddressType,
    apartmentNumber,
    selectedLatLng,
    selectedAddressText,
  } = useSelector((state: RootState) => state.csFlow)
  
  // Get user from auth for branch ID fallback
  const { user } = useSelector((state: RootState) => state.auth)

  const selectedTypeId = useSelector(selectSelectedTypeId)
  const isRTL = useSelector((state: RootState) => state.settings.settings.language === 'ar')
  const cartId = useSelector(selectCsCartId)

  // RTK Query hooks for API calls
  const {
    data: branchesData,
    isLoading: isLoadingBranches,
    error: branchesError,
    refetch: refetchBranches,
  } = useGetEmployeeBranchesQuery(
    {},
    {
      skip: !isOpen, // Only fetch when modal is open
      refetchOnMountOrArgChange: true,
    }
  )

  const {
    data: addressesData,
    isLoading: isLoadingAddresses,
    error: addressesError,
    refetch: refetchAddresses,
  } = useGetCustomerAddressesQuery(
    { userId: customerId },
    {
      skip: !isOpen || customerId <= 0, // Only fetch when modal is open and customer exists
      refetchOnMountOrArgChange: true,
    }
  )

  const {
    data: carData,
    isLoading: isLoadingCarData,
    error: carDataError,
    refetch: refetchCarData,
  } = useGetCarModelsAndColorsQuery(
    {},
    {
      // Enable query when popup is open and order type is car pickup (typeId === 4)
      skip: !isOpen || selectedTypeId !== 4,
      refetchOnMountOrArgChange: true,
    }
  )

  const [changeCartType, { isLoading: isChangingOrderType }] = useChangeCartTypeMutation()

  // Transform API data to match component expectations
  const branches = useMemo(() => {
    if (!branchesData?.data?.branches) return []

    return branchesData.data.branches.map((branch: any) => {
      const activeFlag = normalizeBooleanValue(branch.active ?? branch.Active)
      const busyFlag = normalizeBooleanValue(branch.busy ?? branch.Busy)
      const isOpenFlag = normalizeBooleanValue(branch.is_open ?? branch.isOpen)

      const explicitAvailabilityRaw =
        branch.is_available ?? branch.isAvailable ?? branch.available
      const hasExplicitAvailability = explicitAvailabilityRaw !== undefined && explicitAvailabilityRaw !== null
      const explicitAvailability = hasExplicitAvailability
        ? normalizeBooleanValue(explicitAvailabilityRaw)
        : undefined

      const availability = explicitAvailability !== undefined
        ? explicitAvailability
        : activeFlag && !busyFlag && isOpenFlag

      return {
        id: branch.id,
        name: branch.name,
        descriptiveAddress: branch.descriptive_address ?? branch.descriptiveAddress ?? '',
        status: branch.status ?? branch.Status ?? '',
        display_name: branch.name,
        area: '',
        city: '',
        active: activeFlag,
        busy: busyFlag,
        is_open: isOpenFlag,
        working_from: branch.working_from ?? branch.workingFrom ?? '',
        working_to: branch.working_to ?? branch.workingTo ?? '',
        is_available: availability,
      }
    })
  }, [branchesData])

  const savedAddresses = useMemo(() => {
    if (!addressesData?.data) return []

    return addressesData.data.map((address: any) => {
      const descriptiveAddress =
        address.descriptive_address ??
        address.descriptiveAddress ??
        address.description ??
        ''

      const addressName =
        address.addressName ??
        address.name ??
        address.label ??
        ''

      const shortLabel = address.label ?? ''

      const addressText =
        address.address ??
        address.streetName ??
        address.descriptiveAddress ??
        ''

      return {
        id: address.id,
        label: shortLabel,
        addressName,
        address: addressText,
        descriptiveAddress,
        type: address.type,
        latitude: address.latitude,
        longitude: address.longitude,
      }
    })
  }, [addressesData])

  const carModels = useMemo(() => {
    if (!carData?.data?.models) return []
    return carData.data.models
  }, [carData])

  const carColors = useMemo(() => {
    if (!carData?.data?.colors) return []
    return carData.data.colors
  }, [carData])

  // Load order types when modal opens (from localStorage)
  useEffect(() => {
    if (isOpen) {
      dispatch(loadOrderTypes() as any)
    }
  }, [isOpen, dispatch])

  // Handle API errors
  useEffect(() => {
    if (branchesError) {
      toast.error(t('cs.failed_to_load_branches') || 'فشل في تحميل الفروع', {
        duration: 3000,
        position: 'top-center',
      })
    }
  }, [branchesError, t])

  useEffect(() => {
    if (addressesError) {
      // Don't show error toast for addresses if customer has no addresses
    }
  }, [addressesError])

  useEffect(() => {
    if (carDataError) {
      toast.error(t('cs.failed_to_load_car_data') || 'فشل في تحميل بيانات السيارة', {
        duration: 3000,
        position: 'top-center',
      })
    }
  }, [carDataError, t])

  // Filter branches based on search
  const filteredBranches = branches.filter((branch) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    const name = branch.name?.toLowerCase() || ''
    const address = branch.descriptiveAddress?.toLowerCase() || ''
    return name.includes(query) || address.includes(query)
  })

  // Handle order type selection
  const handleOrderTypeSelect = (orderType: { id: number; name: string }) => {
    dispatch(selectOrderType(orderType.id.toString()))
  }

  // Handle branch selection
  const handleBranchSelect = (branch: any) => {
    dispatch(markBranchSelected(branch.id))
    localStorage.setItem('selected_branch_data', JSON.stringify(branch))
    setCurrentStep('main')
  }

  // Handle address selection
  const handleAddressSelect = (address: any) => {
    dispatch(setSelectedAddressId(address.id))
    dispatch(markAddressSelected())
  }

  // Handle continue button
  const handleContinue = async () => {
    if (!canContinue()) {
      return
    }

    setIsSubmitting(true)

    try {
      const typeId = selectedTypeId
      // استخدام selectedBranchId إذا كان موجوداً، وإلا استخدام branch ID من auth
      let branchId = selectedBranchId
      if (!branchId) {
        // Fallback: الحصول على branch ID من auth
        if (user?.branch?.id) {
          branchId = typeof user.branch.id === 'number' ? user.branch.id : Number(user.branch.id)
        } else {
          // Fallback آخر: من localStorage
          try {
            const employeeDataStr = localStorage.getItem('employee_data')
            if (employeeDataStr) {
              const employeeData = JSON.parse(employeeDataStr)
              if (employeeData.branch?.id) {
                branchId = typeof employeeData.branch.id === 'number' 
                  ? employeeData.branch.id 
                  : Number(employeeData.branch.id)
              }
            }
          } catch (e) {
          }
        }
        if (!branchId || branchId <= 0) {
          toast.error('يرجى اختيار الفرع')
          setIsSubmitting(false)
          return
        }
      }

      // Save branch selection
      if (selectedBranchId) {
        const selectedBranch = branches.find((b) => b.id === selectedBranchId)
        if (selectedBranch) {
          localStorage.setItem('selected_branch_data', JSON.stringify(selectedBranch))
        }
      }

      // Save address selection for delivery orders
      if (typeId === 1 && selectedAddressId) {
        localStorage.setItem('selected_address_id', selectedAddressId.toString())
      }

      // Call API to change order type
      if (customerId > 0) {
        if (cartId) {
          try {
            // Ensure cartId is a string
            const cartIdString = cartId.toString()
            
            await changeCartType({
              cartId: cartIdString,
              userId: customerId,
              typeId,
              branchId,
              addressId: typeId === 1 ? selectedAddressId || undefined : undefined,
            }).unwrap()

          // Update Redux state to reflect the changes
          // IMPORTANT: Update state BEFORE any async operations to ensure isCsFlowComplete becomes true
          
          // Update state synchronously to ensure immediate propagation
          dispatch(updateOrderTypeFromId(typeId))
          dispatch(markBranchSelected(branchId))
          
          // Save car details for car pickup orders
          if (typeId === 4) {
            dispatch(
              setCarDetails({
                color: carColor,
                type: '',
                number: carNumber,
                brand: carBrand,
                logo: carLogo,
                model: carModel || carBrand,
              })
            )
          }

          // CRITICAL: Mark order details as completed - this allows menu to appear
          dispatch(markOrderDetailsCompleted())

          // Refresh cart items
          await dispatch(refreshCsCartItems({ customerId, forceRefresh: true }) as any)

          toast.success(
            t('cs.order_type_updated') || 'تم تحديث نوع الطلب بنجاح',
            {
              duration: 2000,
              position: 'top-center',
            }
          )

          setIsSubmitting(false)
          // Close popup - RTK Query will automatically fetch menu when skip condition changes
          onClose()
          } catch (error: any) {
            // Extract error message
            let errorMessage = t('cs.failed_to_update_order_type') || 'فشل في تحديث نوع الطلب'
            if (error?.data?.message) {
              errorMessage = error.data.message
            } else if (error?.data?.descriptiveMessage) {
              errorMessage = error.data.descriptiveMessage
            } else if (error?.message) {
              errorMessage = error.message
            } else if (typeof error?.data === 'string') {
              errorMessage = error.data
            }
            
            // Even if API call fails, update state locally and continue
            // This allows the user to proceed and the menu will open
            dispatch(updateOrderTypeFromId(typeId))
            dispatch(markBranchSelected(branchId))
            
            // Save car details for car pickup orders
            if (typeId === 4) {
              dispatch(
                setCarDetails({
                  color: carColor,
                  type: '',
                  number: carNumber,
                brand: carBrand,
                logo: carLogo,
                model: carModel || carBrand,
                })
              )
            }
            
            // CRITICAL: Mark order details as completed - this allows menu to appear
            dispatch(markOrderDetailsCompleted())
            
            // Show warning instead of error since we're continuing anyway
            toast.error(errorMessage, {
              duration: 3000,
              position: 'top-center',
            })
            
            setIsSubmitting(false)
            // Close modal - RTK Query will automatically fetch menu when skip condition changes
            onClose()
            return
          }
        } else {
          // If no cart, just update locally
          dispatch(updateOrderTypeFromId(typeId))
          dispatch(markBranchSelected(branchId))
          
          // Save car details for car pickup orders
          if (typeId === 4) {
            dispatch(
              setCarDetails({
                color: carColor,
                type: '',
                number: carNumber,
                brand: carBrand,
                logo: carLogo,
                model: carModel || carBrand,
              })
            )
          }

          // CRITICAL: Mark order details as completed - this allows menu to appear
          dispatch(markOrderDetailsCompleted())

          toast.success(
            t('cs.order_type_updated') || 'تم تحديث نوع الطلب بنجاح',
            {
              duration: 2000,
              position: 'top-center',
            }
          )
          
          setIsSubmitting(false)
          // Close popup - RTK Query will automatically fetch menu when skip condition changes
          onClose()
        }
      } else {
        // If no customer, just update locally
        dispatch(updateOrderTypeFromId(typeId))
        dispatch(markBranchSelected(branchId))
        
        // Save car details for car pickup orders
        if (typeId === 4) {
          dispatch(
            setCarDetails({
              color: carColor,
              type: '',
              number: carNumber,
              brand: carBrand,
              logo: carLogo,
              model: carModel || carBrand,
            })
          )
        }

        // CRITICAL: Mark order details as completed - this allows menu to appear
        dispatch(markOrderDetailsCompleted())

        toast.success(
          t('cs.order_type_updated') || 'تم تحديث نوع الطلب بنجاح',
          {
            duration: 2000,
            position: 'top-center',
          }
        )
        
        setIsSubmitting(false)
        // Close popup - RTK Query will automatically fetch menu when skip condition changes
        onClose()
      }
    } catch (error: any) {
      toast.error(
        t('cs.error_updating_order_type') || `حدث خطأ أثناء تحديث نوع الطلب: ${error.message || 'خطأ غير معروف'}`,
        {
          duration: 3000,
          position: 'top-center',
        }
      )
      setIsSubmitting(false)
      // Don't close modal on error - let user retry
    }
  }

  // Get selected branch and address
  const selectedBranch = useMemo(() => {
    return branches.find((b) => b.id === selectedBranchId) || null
  }, [branches, selectedBranchId])

  const selectedAddress = useMemo(() => {
    return savedAddresses.find((a) => a.id === selectedAddressId) || null
  }, [savedAddresses, selectedAddressId])

  // Filter car colors and models
  const filteredCarColors = useMemo(() => {
    if (!carColorSearchQuery) return carColors
    const query = carColorSearchQuery.toLowerCase()
    return carColors.filter((color) => {
      const name = typeof color.name === 'string' 
        ? color.name 
        : (color.name?.ar || color.name?.en || '').toLowerCase()
      return name.toLowerCase().includes(query)
    })
  }, [carColors, carColorSearchQuery])

  const filteredCarModels = useMemo(() => {
    if (!carTypeSearchQuery) return carModels
    const query = carTypeSearchQuery.toLowerCase()
    return carModels.filter((model) => {
      let brandName = ''
      if (typeof model.brand === 'string') {
        brandName = model.brand
      } else if (model.brand && typeof model.brand === 'object') {
        // API structure: brand: { en: string, ar: string }
        if ('en' in model.brand || 'ar' in model.brand) {
          brandName = (model.brand.ar || model.brand.en || '')
        } else {
          // Fallback for other structures
          brandName = ((model.brand as any)?.name?.ar || (model.brand as any)?.name?.en || (model.brand as any)?.name || '')
        }
      }
      return brandName.toLowerCase().includes(query)
    })
  }, [carModels, carTypeSearchQuery])

  // Check if can continue
  const canContinue = (): boolean => {
    return canContinueUtil({
      selectedOrderType,
      selectedBranch,
      selectedAddress,
      carColor,
      carBrand,
      carNumber,
      selectedTypeId,
    })
  }

  // Get header title based on current step
  const headerTitle = getHeaderTitle(currentStep, t)

  // Handle header button (back/close)
  const handleHeaderButton = () => {
    if (currentStep === 'main') {
      onClose()
    } else {
      setCurrentStep('main')
    }
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel
          className={`
            w-[38%] max-w-2xl h-[90vh] bg-white rounded-[25px]
            flex flex-col overflow-hidden
          `}
        >
          {/* Header - New simple header for main step */}
          {addressStep !== 'addNewAddress' && currentStep === 'main' && (
            <div className="px-[18px] pt-[18px] pb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold font-['IBM_Plex_Sans_Arabic'] text-gray-900">
                {t('cs.order_details') || 'تفاصيل الطلب '}
              </h2>
              <button
                onClick={onClose}
                className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-gray-300 transition-colors hover:bg-gray-50"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          )}

          {/* Header - Old header with back button for other steps */}
          {addressStep !== 'addNewAddress' && currentStep !== 'main' && (
            <div className="px-[18px] pt-[18px] pb-4 flex items-center justify-start gap-2 flex-shrink-0">
              <button
                onClick={handleHeaderButton}
                className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-gray-300 transition-colors hover:bg-gray-50"
              >
                {isRTL ? <ChevronRight className="w-5 h-5 text-gray-600" /> : <ChevronLeft className="w-5 h-5 text-gray-600" />}
              </button>
              <h2 className="text-lg font-semibold font-['IBM_Plex_Sans_Arabic'] text-gray-900">
                {headerTitle}
              </h2>
              <div className="w-10" /> {/* Spacer for centering */}
            </div>
          )}
          {/* Main Content */}
          {currentStep === 'main' && (
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {/* Order Type Selection */}
              {isOrderTypesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]"></div>
                </div>
              ) : (
                buildOrderTypeSection({
                  selectedOrderType,
                  orderTypes,
                  onOrderTypeSelected: handleOrderTypeSelect,
                  isRTL,
                })
              )}

              <div className="h-[1px]" />

              {/* Delivery address section (only for delivery orders) */}
              {selectedOrderType &&
                (selectedOrderType.includes('delivery') || selectedTypeId === 1) &&
                buildAddressSelectionSection({
                  selectedAddress: selectedAddress as any,
                  selectedAddressText,
                  onTap: async () => {
                    if (customerId > 0) {
                      try {
                        const addressesResult = await refetchAddresses()

                        if (addressesResult.data?.data && addressesResult.data.data.length > 0) {
                          dispatch(setAddressStep('savedAddresses'))
                        } else {
                          dispatch(setAddressStep('noAddresses'))
                        }
                      } catch (error) {
                        dispatch(setAddressStep('noAddresses'))
                      }
                    } else {
                      dispatch(setAddressStep('noAddresses'))
                    }
                    setCurrentStep('address')
                  },
                  onClear: () => {
                    dispatch(clearAddressSelection())
                  },
                  isRTL,
                })}

              {/* Car details section (only for car pickup orders) */}
              {selectedOrderType &&
                (selectedOrderType.includes('car pickup') || selectedTypeId === 4) && (
                  <div className="mt-3">
                    {buildCarDetailsSection({
                      carNumber,
                      carColor,
                      carBrand,
                      carLogo,
                      onCarNumberChange: (value) =>
                        dispatch(
                          setCarDetails({
                            color: carColor,
                            type: '',
                            number: value,
                            brand: carBrand,
                            logo: carLogo,
                            model: carModel,
                          })
                        ),
                      onCarColorTap: () => {
                        setCurrentStep('carColor')
                        // Refetch only if data is not available
                        if (carColors.length === 0 && !isLoadingCarData) {
                          refetchCarData().catch(() => {})
                        }
                      },
                      onCarTypeTap: () => {
                        setCurrentStep('carType')
                        // Refetch only if data is not available
                        if (carModels.length === 0 && !isLoadingCarData) {
                          refetchCarData().catch(() => {})
                        }
                      },
                      onClearColor: () =>
                        dispatch(
                          setCarDetails({
                            color: '',
                            type: '',
                            number: carNumber,
                            brand: carBrand,
                            logo: carLogo,
                            model: carModel,
                          })
                        ),
                      onClearBrand: () =>
                        dispatch(
                          setCarDetails({
                            color: carColor,
                            type: '',
                            number: carNumber,
                            brand: '',
                            logo: '',
                            model: '',
                          })
                        ),
                      isRTL,
                    })}
                </div>
              )}

              <div className="h-3" />

              {/* Branch selection (always required) */}
              {buildBranchSelectionSection({
                selectedBranch: selectedBranch as any,
                onTap: () => setCurrentStep('branch'),
              onClear: () => {
                dispatch(clearBranchSelection())
              },
                isRTL,
              })}
            </div>
          )}

          {/* Branch Selection Step */}
          {currentStep === 'branch' &&
            buildBranchSelectionContent({
              isLoadingBranches,
              filteredBranches: filteredBranches as any,
              branches: branches as any,
              selectedBranch: selectedBranch as any,
              searchQuery,
              onSearchChanged: setSearchQuery,
              onBranchSelected: handleBranchSelect,
              onDone: () => setCurrentStep('main'),
              isRTL,
            })}

          {/* Address Selection Step */}
          {currentStep === 'address' &&
            buildCompleteAddressFlow({
              isLoadingAddresses,
              addressStep,
              savedAddresses: savedAddresses as any,
              selectedAddress: selectedAddress as any,
              onAddressSelected: handleAddressSelect,
              onAddNewAddress: () => {
                dispatch(setAddressStep('addNewAddress'))
              },
              onDone: () => {
                setCurrentStep('main')
                dispatch(setAddressStep('savedAddresses'))
              },
              onAddressSaved: () => {
                // Immediately navigate to saved addresses page
                dispatch(setAddressStep('savedAddresses'))
                // Refetch addresses to get the newly saved address (async, don't wait)
                refetchAddresses().catch(() => {
                  // Ignore refetch errors
                })
              },
              onBack: () => {
                if (addressStep === 'addNewAddress') {
                  dispatch(setAddressStep(savedAddresses.length > 0 ? 'savedAddresses' : 'noAddresses'))
                } else {
                  setCurrentStep('main')
                }
              },
              userId: customerId,
              isRTL,
            })}

          {/* Car Color Selection Step */}
          {currentStep === 'carColor' &&
            buildCarColorStep({
              carColors,
              filteredCarColors,
              isLoadingCarData,
              carColorSearchQuery,
              selectedCarColor: carColor,
              onColorSelected: (color) => {
                dispatch(
                  setCarDetails({
                    color,
                    type: '',
                    number: carNumber,
                    brand: carBrand,
                    logo: carLogo,
                    model: carModel,
                  })
                )
              },
              onDone: () => setCurrentStep('main'),
              onSearchChange: (query) => dispatch(updateCarColorSearch(query)),
              isRTL,
              currentLanguage: isRTL ? 'ar' : 'en',
            })}

          {/* Car Type/Brand Selection Step */}
          {currentStep === 'carType' &&
            buildCarTypeStep({
              carModels,
              filteredCarModels,
              isLoadingCarData,
              carTypeSearchQuery,
              selectedCarBrand: carBrand,
              onBrandSelected: (brand, logo, modelName) => {
                dispatch(
                  setCarDetails({
                    color: carColor,
                    type: '',
                    number: carNumber,
                    brand,
                    logo,
                    model: modelName || brand,
                  })
                )
              },
              onDone: () => setCurrentStep('main'),
              onSearchChange: (query) => dispatch(updateCarTypeSearch(query)),
              isRTL,
              currentLanguage: isRTL ? 'ar' : 'en',
            })}

          {/* Footer with Continue Button */}
          {currentStep === 'main' && (
            <div className="px-5 pb-5 pt-4">
              {buildContinueButton({
                canContinue: canContinue(),
                onPressed: handleContinue,
                isSubmitting,
              })}
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default CsOrderTypeSelectionPopup

